import { useMemo } from 'react';
import { useMaterials } from '@/hooks/useMaterials';
import { useHiddenMaterials } from '@/hooks/useHiddenMaterials';
import { findStructureMatches, oppositeStructureCategory, type StructureMatchCandidate } from './findStructureMatch';
import type { LayerV2 } from '../types';

/**
 * Rileva se alla composizione manca la guida (o il montante) e propone dei
 * candidati, SENZA inserire niente.
 *
 * Perche' esiste. Prima il configuratore, appena sceglievi un montante,
 * infilava da solo una guida presa dal catalogo. Il prodotto scelto era
 * spesso sbagliato (vedi `findStructureMatch.ts`) e finiva in composizione
 * senza che nessuno lo avesse approvato. Ora si segnala e basta: la scelta
 * resta all'utente.
 *
 * Regola: una parete con orditura metallica ha sempre sia montanti sia guide.
 * Se c'e' l'uno senza l'altro, manca qualcosa. Una controparete a incollaggio
 * non ha ne' montanti ne' guide e quindi non produce nessun avviso.
 */

export interface MissingStructurePartner {
  /** Categoria che manca, oppure null se la composizione e' completa. */
  missing: 'structure_frame' | 'structure_guide' | null;
  /** Il profilo gia' presente da cui si derivano i candidati. */
  reference: LayerV2 | null;
  /** Prodotti proposti (max 3), gia' filtrati e ordinati. */
  candidates: StructureMatchCandidate[];
}

const EMPTY: MissingStructurePartner = { missing: null, reference: null, candidates: [] };

export function useMissingStructurePartner(layers: LayerV2[]): MissingStructurePartner {
  const { data: catalog = [] } = useMaterials();
  const { hiddenSet } = useHiddenMaterials();

  return useMemo(() => {
    const frames = layers.filter(l => l.material?.category === 'structure_frame');
    const guides = layers.filter(l => l.material?.category === 'structure_guide');

    // Nessuna struttura, oppure struttura completa: niente da segnalare.
    if (frames.length === 0 && guides.length === 0) return EMPTY;
    if (frames.length > 0 && guides.length > 0) return EMPTY;

    const reference = frames.length > 0 ? frames[0] : guides[0];
    const refMaterial = reference?.material;
    if (!refMaterial) return EMPTY;

    const missing = oppositeStructureCategory(
      refMaterial.category as 'structure_frame' | 'structure_guide',
    );

    const candidates = catalog.length > 0
      ? findStructureMatches(refMaterial, catalog, missing, { hiddenIds: hiddenSet })
      : [];

    return { missing, reference, candidates };
  }, [layers, catalog, hiddenSet]);
}
